import omit from "lodash/omit";
import { Grid, Box } from "@mui/material";
import config from "api/config";
import {
  useGetProcedureCodes,
  ProcedureCode,
  UseGetProcedureCodesProps,
  useGetAuthCategories,
  MissingInformationOutreachConfigurationResponse,
  useSearchOutreachAttempts,
  useGetProvidersOrFacilities,
  ProviderFacilityCatalogue,
  UseGetProvidersOrFacilitiesProps,
} from "@coherehealth/core-platform-api";
import {
  useGetIncompleteQueueVolume,
  ServiceCase,
  useSearchServiceCasesInQueue,
  ServiceCaseSearch,
  User,
  useSearchUser,
  useUpdateServiceCaseAssignee,
  useSearchServiceCaseCountInQueue,
  useGetSkillsByNamePattern,
  QueueViewState,
  UserSkill,
  UseGetSkillsByNamePatternProps,
  GetSkillsByNamePatternQueryParams,
  Queue,
  useSearchPatientNames,
  PatientName,
} from "@coherehealth/qm-api";
import {
  CohereTable,
  DEFAULT_MAX_DISPLAY,
  SelectOptionsHook,
  useLazyLoadingQueryOptionsHook,
  LazyLoadingGetProps,
  useSyncQueryParamsToPath,
  useParsedQueryString,
  ReassignModal,
  UnassignModal,
  MultiDeleteModal,
  Chip,
  getCaseRedirectUrl,
  FilterValues,
  filterApiParamNames,
  FilterConfig,
  isNonNullable,
  FiltersPopover,
  filterApiParams,
  initialFilterValues,
  useEffectDeepEquality,
  store,
  useFeature,
  SingleSelect,
  MultiUnassignModal,
  computeHoldReasonOptions,
  ChipType,
  useConfiguration,
  translateServiceCaseHealthPlan,
} from "@coherehealth/common";
import PageHeader from "../common/PageHeader";

import { QueueVolumeMetric, TeamWorkedMetric } from "../DashboardPage/DashboardMetrics";
import { useCallback, useEffect, useState, useContext, useMemo } from "react";
import { useSnackbar } from "notistack";
import { warn as logWarning } from "logger";
import { KeyboardArrowDown } from "@material-ui/icons";
import {
  tableColumnsConfig,
  FilterConfigInjectedDeps,
  NO_REQUIRED_SKILLS_VALUE,
  tableColumnToFilterConfig,
} from "../../util/TableUtil";
import { UserContext } from "UserContext";
import { queueDisplayName, showSupervisorSearchConfig } from "../../util/QueueUtil";
import { useIsInternalCohereUser } from "../../util/authorization";
import { useLaunchDarklyFeatureFlag } from "../../hooks/useLaunchDarklyFeatureFlag";
import QueueSelectionModal from "../common/modals/QueueSelectionModal";
import DashboardTitle from "../common/DashboardTitle";
import { useAuthorized } from "Authorization";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import UnassignIcon from "@mui/icons-material/Inbox";
import useMultiCaseActions from "./hooks/useMultiCaseActions";
import { buildAttemptsBySr } from "./outreachEnrichment";
import DeleteButton from "./MassActionButtons/DeleteButton";
import UnassignButton from "./MassActionButtons/UnassignButton";
import { UploadCase } from "./UploadCase/UploadCase";
import { useGetAccessToken } from "@coherehealth/common";
import { useOktaAuth } from "@okta/okta-react";
// Single query parameter type
type QueryParam = ReturnType<typeof useParsedQueryString> extends { [key: string]: infer T } ? T : never;
// Type for a function which takes in a query parameter and returns a given type
type ParamParser<T> = (ipt: QueryParam, defaultVal: T) => T;

const parseNumberFromQs: ParamParser<number> = (ipt, defaultVal) => (ipt !== undefined ? Number(ipt) : defaultVal);
const parseStringArrayFromQs: ParamParser<string[]> = (ipt, defaultVal) =>
  Array.isArray(ipt) ? ipt.map((x) => `${x}`) : defaultVal;
const parseBoolFromQs: ParamParser<boolean> = (ipt, defaultVal) =>
  ipt === "true" ? true : ipt === "false" ? false : defaultVal;

const SupervisorDashboard = () => {
  // Check if the feature flag is enabled
  const allowFiltersPersistanceOnQueueSection = useFeature("allowFiltersPersistanceOnQueueSection");

  // Retrieve the current context values
  const { activeQueue, queueView } = useContext(UserContext);

  useEffect(() => {
    const navigationEntries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    const isPageReloaded =
      Array.isArray(navigationEntries) && navigationEntries.length > 0 && navigationEntries[0]?.type === "reload";
    if (isPageReloaded) {
      store.remove("SupervisorDashboardQueryParams");
      store.remove("SupervisorDashboardCurrentQueueTabFilterValues");
      setQueryParams({
        max: 21,
        sortBy: [],
        sortDesc: true,
        offset: 0,
      });
    }
  }, []);

  const defaultSortBy = useMemo(
    () => (queueView === "RN_REVIEW" ? ["serviceRequest.tatDueTimestamp"] : ["dueDate"]),
    [queueView]
  );
  const [displayQueueSelectionModal, setDisplayQueueSelectionModal] = useState(false);
  const showSupervisorSearch = showSupervisorSearchConfig(queueView, "SUPERVISOR_DASHBOARD");

  // Initial setup for queryParams based on query string values
  const qs = useParsedQueryString();
  let initialQueryParams = {
    max: parseNumberFromQs(qs.max, DEFAULT_MAX_DISPLAY + 1),
    sortBy: parseStringArrayFromQs(qs.sortBy, defaultSortBy),
    sortDesc: parseBoolFromQs(qs.sortDesc, false),
    offset: parseNumberFromQs(qs.offset, 0),
  };

  // Comment explaining how store loading works
  /**
   * The following logic checks if the feature flag 'allowFiltersPersistanceOnQueueSection' is enabled.
   * If it is, the application will try to load the previously saved state from the store (localStorage) for 'SupervisorDashboardQueryParams'.
   * This allows the user to maintain their filter and query parameters across sessions or page reloads.
   * If no saved state is found, it falls back to the default query parameters initialized above.
   */
  if (allowFiltersPersistanceOnQueueSection) {
    const savedQueryParams = store.get("SupervisorDashboardQueryParams")
      ? store.get("SupervisorDashboardQueryParams")
      : null;
    initialQueryParams = savedQueryParams || initialQueryParams;
  }

  const [queryParams, setQueryParams] = useState<ServiceCaseSearch>(initialQueryParams);
  const [hasInitialized, setHasInitialized] = useState(false);

  // UseEffect to handle initial load and cleanup of the store
  useEffect(() => {
    if (!allowFiltersPersistanceOnQueueSection) {
      store.remove("SupervisorDashboardQueryParams");
      store.remove("SupervisorDashboardCurrentQueueTabFilterValues");
    }
  }, [allowFiltersPersistanceOnQueueSection]);

  useSyncQueryParamsToPath(queryParams);

  useEffect(() => {
    if (allowFiltersPersistanceOnQueueSection && !hasInitialized && store.get("SupervisorDashboardQueryParams")) {
      setQueryParams(store.get("SupervisorDashboardQueryParams"));
    }
    setHasInitialized(true);
  }, [allowFiltersPersistanceOnQueueSection, hasInitialized]);

  useEffect(() => {
    if (allowFiltersPersistanceOnQueueSection && hasInitialized) {
      store.set("SupervisorDashboardQueryParams", queryParams);
    }
  }, [allowFiltersPersistanceOnQueueSection, queryParams, hasInitialized]);

  return (
    <>
      {!activeQueue ? null : (
        <>
          <Grid container sx={{ padding: 4, flexDirection: "column" }}>
            <Box>
              <DashboardTitle title={"Queues"} applyPadding={!showSupervisorSearch} />
              <PageHeader
                headerLabel={queueDisplayName(queueView, activeQueue.displayConfig)}
                tertiaryButtonTestid="qm-button-change-queue"
                tertiaryButtonLabel="Change queue"
                tertiaryButtonAction={() => setDisplayQueueSelectionModal(true)}
                hideBottomLine
                supervisorSearch={showSupervisorSearch}
                routeConfigByCaseType={activeQueue?.routeConfigByCaseType}
                activeQueueId={activeQueue.id}
                guardrails={activeQueue.guardrails}
              />
            </Box>

            <Box>
              <CurrentQueueTab
                queueView={queueView}
                activeQueue={activeQueue}
                defaultSortBy={defaultSortBy}
                queryParams={queryParams}
                setQueryParams={setQueryParams}
              />
            </Box>
            <QueueSelectionModal
              isOpen={displayQueueSelectionModal}
              closeModal={() => setDisplayQueueSelectionModal(false)}
              setQueryParams={setQueryParams}
            />
          </Grid>
        </>
      )}
    </>
  );
};

interface Props {
  queueView: QueueViewState;
  defaultSortBy: string[];
  queryParams: ServiceCaseSearch;
  setQueryParams: React.Dispatch<React.SetStateAction<ServiceCaseSearch>>;
  activeQueue: Queue | undefined;
}

const CurrentQueueTab = ({ queueView, defaultSortBy, queryParams, setQueryParams, activeQueue }: Props) => {
  const {
    id: activeQueueId,
    displayConfig: queueDisplayConfig,
    routeConfigByCaseType,
    allowUploads,
    holdReasons,
    massActionsEnabled,
  } = activeQueue || {};

  const allowFiltersPersistanceOnQueueSection = useFeature("allowFiltersPersistanceOnQueueSection");
  const { enqueueSnackbar } = useSnackbar();
  const [currentFilterValues, setCurrentFilterValues] = useState<FilterValues>(() => {
    const navigationEntries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    const isPageReloaded =
      Array.isArray(navigationEntries) && navigationEntries.length > 0 && navigationEntries[0]?.type === "reload";

    if (isPageReloaded) {
      store.remove("SupervisorDashboardCurrentQueueTabFilterValues");
      return initialFilterValues([], false);
    }

    return allowFiltersPersistanceOnQueueSection && store.get("SupervisorDashboardCurrentQueueTabFilterValues")
      ? store.get("SupervisorDashboardCurrentQueueTabFilterValues")
      : initialFilterValues([], false);
  });

  useEffect(() => {
    if (allowFiltersPersistanceOnQueueSection) {
      const savedFilterValues = store.get("SupervisorDashboardCurrentQueueTabFilterValues");
      if (savedFilterValues) {
        setCurrentFilterValues(savedFilterValues);
      }
    }
  }, [allowFiltersPersistanceOnQueueSection, activeQueueId]);

  // Gets the current saved filter value *ONLY IF FEATURE FLAG IS ENABLED*
  const savedFilterValues: FilterValues | null = allowFiltersPersistanceOnQueueSection
    ? store.get("SupervisorDashboardCurrentQueueTabFilterValues")
      ? store.get("SupervisorDashboardCurrentQueueTabFilterValues")
      : null
    : null;

  /**
   * The following useEffect checks if filter persistence is enabled via the feature flag.
   * If so, it tries to load saved filter values from the store (localStorage) and updates
   * the current filter values accordingly. This allows users to retain their filter selections
   * components or page reloads.
   */
  useEffectDeepEquality(() => {
    if (allowFiltersPersistanceOnQueueSection && savedFilterValues) {
      setCurrentFilterValues(savedFilterValues);
      store.set("SupervisorDashboardCurrentQueueTabFilterValues", { ...savedFilterValues, activeQueueId });
    }
  }, [allowFiltersPersistanceOnQueueSection, savedFilterValues]);

  const [agent, setAgent] = useState<User | null>(null);

  const [openReassign, setOpenReassign] = useState(false);
  const [openUnassign, setOpenUnassign] = useState(false);
  const [openMultiDelete, setOpenMultiDelete] = useState(false);
  const [openMultiUnassign, setOpenMultiUnassign] = useState(false);
  const [isAssigneeDropDownOpen, setIsAssigneeDropDownOpen] = useState<Record<string, boolean>>({});

  const [serviceCases, setServiceCases] = useState<ServiceCase[]>([]);
  const [missingInfoConfigs, setMissingInfoConfigs] = useState<
    Record<string, MissingInformationOutreachConfigurationResponse>
  >({});
  const [totalCount, setTotalCount] = useState<number | undefined>();
  const [selectedServiceCase, setSelectedServiceCase] = useState<ServiceCase | null>(null);

  // Extract unique health plan/delegated vendor combos from service cases
  const uniqueHealthPlanVendorCombos = useMemo(() => {
    if (serviceCases.length === 0) {
      return [];
    }
    const combos = serviceCases
      .map((sc) => ({ healthPlanName: sc.healthPlan, delegatedVendor: sc.delegatedVendor }))
      .filter(
        (combo, index, arr) =>
          arr.findIndex(
            (c) => c.healthPlanName === combo.healthPlanName && c.delegatedVendor === combo.delegatedVendor
          ) === index
      );
    return combos;
  }, [serviceCases]);

  // A queue is scoped to a single health plan / delegated vendor, so evaluate enablePostClaimRetroAuths once at
  // the page level with that context (drives both the Request Type "Post-claim retro" label and the filter option).
  const queueHealthPlanVendor = uniqueHealthPlanVendorCombos[0];
  const { featureFlagEnabled: enablePostClaimRetroAuths } = useLaunchDarklyFeatureFlag({
    featureFlagName: "enablePostClaimRetroAuths",
    context:
      queueHealthPlanVendor?.healthPlanName || queueHealthPlanVendor?.delegatedVendor
        ? {
            // serviceCase.healthPlan is the QM code; translate to the Core Platform health plan name that LD targets on.
            healthPlanName: translateServiceCaseHealthPlan(queueHealthPlanVendor.healthPlanName),
            delegatedVendorName: queueHealthPlanVendor.delegatedVendor,
          }
        : undefined,
  });

  const { oktaAuth } = useOktaAuth();
  const getAccessToken = useGetAccessToken(oktaAuth);

  const isCohereUser = useIsInternalCohereUser();
  const { data: authCategoriesData } = useGetAuthCategories({ base: config.SERVICE_API_URL });
  const authCategoriesOptions =
    authCategoriesData?.map((i) => ({
      id: i.enumName,
      label: i.name,
    })) || [];

  const useUsers: SelectOptionsHook<User> = (selectOptionsParams) =>
    useLazyLoadingQueryOptionsHook({
      ...selectOptionsParams,
      useGetHook: useSearchUser,
      additionalQueryParams: {
        name: selectOptionsParams.query,
        supportOnly: false,
      },
      optionsDecorator: (opts) => {
        const query = selectOptionsParams.query;
        if (selectedServiceCase?.assignee?.name) {
          const options = opts && opts.length > 0 ? (query === "" ? [{ name: "Unassign" }, ...opts] : opts) : [];
          return options;
        } else {
          return opts;
        }
      },
    });

  const useFilterUsers: SelectOptionsHook<User> = (selectOptionsParams) => {
    const extraOpts = [
      { name: "Unassigned" } as User,
      { name: "Unassigned - Unable to work" } as User,
      { name: "Unassigned - hold in queue" } as User,
    ];
    const data = useLazyLoadingQueryOptionsHook({
      ...selectOptionsParams,
      useGetHook: useSearchUser,
      additionalQueryParams: {
        name: selectOptionsParams.query,
        supportOnly: false,
      },
      optionsDecorator: (opts) => {
        return [...extraOpts, ...(opts || [])];
      },
    });
    return data;
  };

  const useFilterPatients: SelectOptionsHook<PatientName> = (selectOptionsParams) => {
    const data = useLazyLoadingQueryOptionsHook({
      ...selectOptionsParams,
      useGetHook: useSearchPatientNames,
      additionalQueryParams: {
        fullName: selectOptionsParams.query,
        sortOrder: "DESC",
      },
      optionsDecorator: (opts) => {
        return opts?.map((opt) => ({ name: opt as string }));
      },
    });
    return data;
  };

  const {
    data: queueVolume,
    error: queueVolumeError,
    refetch: getIncompleteQueueVolume,
  } = useGetIncompleteQueueVolume({
    lazy: true,
    queueId: activeQueueId || "",
  });

  useEffect(() => {
    if (activeQueueId) {
      getIncompleteQueueVolume();
    }
  }, [activeQueueId, getIncompleteQueueVolume]);

  const {
    loading: serviceCaseSearchLoading,
    error: serviceCaseSearchError,
    refetch: serviceCaseSearch,
  } = useSearchServiceCasesInQueue({
    lazy: true,
    queueId: activeQueueId || "",
  });

  const updateCookies = (filterValues: FilterValues) => {
    if (JSON.stringify(currentFilterValues) !== JSON.stringify(filterValues)) {
      if (filterValues.dateCreated) {
        filterValues.dateCreated = new Date(filterValues.dateCreated as string);
      }

      if (filterValues.dueDate) {
        filterValues.dueDate = new Date(filterValues.dueDate as string);
      }
      setCurrentFilterValues(filterValues);
      if (allowFiltersPersistanceOnQueueSection) {
        store.set("SupervisorDashboardCurrentQueueTabFilterValues", { ...filterValues, activeQueueId });
      }
    }
  };

  const handleFiltersChange = (filterValues: FilterValues) => {
    updateCookies(filterValues);
    const filterKeys = filterApiParamNames(filterConfigs);
    const filterParams = filterApiParams(filterConfigs, filterValues);

    setQueryParams((prevQueryParams) => ({
      ...omit(prevQueryParams, filterKeys),
      ...filterParams,
      offset: 0,
    }));

    resetSelection();
  };

  const { error: serviceCaseSearchCountError, refetch: serviceCaseSearchCount } = useSearchServiceCaseCountInQueue({
    lazy: true,
    queueId: activeQueueId || "",
  });

  const { mutate: updateServiceCaseAssignee, loading: updatingServiceCaseAssignee } = useUpdateServiceCaseAssignee({});

  const assignAgent = useCallback(
    async (agent: User | null, serviceCase: ServiceCase | null) => {
      const updatedCase = await updateServiceCaseAssignee({
        serviceCaseId: serviceCase?.id || "",
        assignee: {
          name: agent?.name || undefined,
          userName: agent?.username || undefined,
        },
        assignmentMethodDetails: {
          assignmentMethod: "MANUAL_ASSIGNMENT",
        },
      });
      setServiceCases((oldValues) => {
        return oldValues.map((element: ServiceCase) => {
          if (element.id === serviceCase?.id) {
            return updatedCase;
          } else {
            return element;
          }
        });
      });
      setAgent(null);
    },
    [updateServiceCaseAssignee]
  );

  const handleOnSelect = useCallback(
    (option: User | null, serviceCase: ServiceCase | null) => {
      setAgent(option);
      setSelectedServiceCase(serviceCase);
      if (serviceCase?.assignee?.name === option?.name) {
        return;
      }
      if (serviceCase?.assignee?.name !== undefined) {
        option?.name !== "Unassign" ? setOpenReassign(true) : setOpenUnassign(true);
      } else {
        assignAgent(option, serviceCase);
      }
    },
    [assignAgent]
  );

  useEffect(() => {
    if (queueVolumeError) {
      enqueueSnackbar(`Failed to load queue volume: ${queueVolumeError.message}`, { variant: "error" });
    }
  }, [enqueueSnackbar, queueVolumeError]);

  useEffect(() => {
    let isMounted = true;
    if (activeQueueId) {
      serviceCaseSearchCount({
        queryParams: {
          ...queryParams,
          caseStatusList: ["IN_PROGRESS", "OPEN"],
        },
        queryParamStringifyOptions: { indices: false },
      }).then((response) => {
        if (isMounted) {
          setTotalCount(response?.count);
        }
      });
    }
    return () => {
      isMounted = false;
    };
  }, [queueView, activeQueueId, serviceCaseSearchCount, queryParams]);

  const optionallyIncludeEscalatedSortingCriteria = useCallback(
    (currentSortBy: string[], sortDesc: boolean | undefined) => {
      const canViewEscalatedChip = isCohereUser && queueDisplayConfig?.showEscalatedChip;
      const shouldIncludeEscalatedSort = canViewEscalatedChip && currentSortBy[0] === "dueDate" && !sortDesc;
      const shouldRemoveEscalatedSort = currentSortBy.includes("isEscalated") && sortDesc;
      let updatedSortBy = [...currentSortBy];
      if (shouldIncludeEscalatedSort && !shouldRemoveEscalatedSort) {
        updatedSortBy = ["isEscalated", ...currentSortBy];
      } else if (!shouldIncludeEscalatedSort && shouldRemoveEscalatedSort) {
        updatedSortBy = currentSortBy.filter((item) => item !== "isEscalated");
      }
      return updatedSortBy;
    },
    [isCohereUser, queueDisplayConfig?.showEscalatedChip]
  );

  useEffect(() => {
    let ignore = false;
    if (isCohereUser !== undefined) {
      const fetchServiceCases = async () => {
        const sortBy = optionallyIncludeEscalatedSortingCriteria(
          queryParams.sortBy ? queryParams.sortBy : defaultSortBy,
          queryParams.sortDesc
        );
        const fetchedServiceCases = await serviceCaseSearch({
          queryParams: {
            ...queryParams,
            sortBy,
            caseStatusList: ["IN_PROGRESS", "OPEN"],
            max: queryParams.max ? queryParams.max - 1 : DEFAULT_MAX_DISPLAY,
            page: (queryParams?.offset || 0) / (queryParams.max ? queryParams.max - 1 : DEFAULT_MAX_DISPLAY),
          },
          queryParamStringifyOptions: { indices: false },
        });
        if (fetchedServiceCases && !ignore) {
          setServiceCases(fetchedServiceCases);
        }
      };

      fetchServiceCases();
      return () => {
        ignore = true;
      };
    }
  }, [defaultSortBy, isCohereUser, optionallyIncludeEscalatedSortingCriteria, queryParams, serviceCaseSearch]);

  useEffect(() => {
    const fetchConfigsForAllHealthPlansAndDelegatedVendor = async () => {
      if (uniqueHealthPlanVendorCombos.length > 0) {
        const configPromises = uniqueHealthPlanVendorCombos.map(async (uhpvc) => {
          try {
            if (!uhpvc.healthPlanName) {
              return { uhpvc, config: null };
            }

            const queryParams = new URLSearchParams({
              featureConfigurationLevel: uhpvc.delegatedVendor ? "DELEGATED_VENDOR" : "HEALTH_PLAN",
              healthPlanName: uhpvc.healthPlanName,
              delegatedVendorName: uhpvc.delegatedVendor ? uhpvc.delegatedVendor : "",
            });

            const accessToken = await getAccessToken();

            const response = await fetch(
              `${config.SERVICE_API_URL}/MissingInformationOutreachConfiguration/configureByHealthPlan?${queryParams}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

            if (!response.ok) {
              // console.log(`HTTP error! status: ${response.status}`);
            }

            const configData = await response.json();
            // console.log(
            //   `Fetched missing information config for ${uhpvc.healthPlanName} and delegated vendor ${uhpvc.delegatedVendor}:`,
            //   configData
            // );
            return { uhpvc, config: configData };
          } catch (error) {
            // console.error(`Failed to fetch config for ${uhpvc.healthPlanName}:`, error);
            return { uhpvc, config: null };
          }
        });
        const results = await Promise.all(configPromises);
        const newConfigs: Record<string, MissingInformationOutreachConfigurationResponse> = {};

        results.forEach(({ uhpvc, config }) => {
          if (config && uhpvc.healthPlanName) {
            const key = `${uhpvc.healthPlanName}${uhpvc.delegatedVendor ? `_${uhpvc.delegatedVendor}` : ""}`;
            newConfigs[key] = config;
          }
        });

        setMissingInfoConfigs(newConfigs);
      }
    };

    fetchConfigsForAllHealthPlansAndDelegatedVendor();
  }, [uniqueHealthPlanVendorCombos, getAccessToken]);

  // RQM-414: Enrich MI cases with live outreach data.
  // The embedded snapshot (previousMICOutreachAttempts) is stale from case creation time.
  // Batch-fetch fresh outreach attempts and merge into state so the table shows current status.
  const { refetch: fetchOutreachAttempts } = useSearchOutreachAttempts({
    lazy: true,
    base: config.SERVICE_API_URL,
  });

  // Derive a stable fingerprint of loaded MI case SR IDs so enrichment re-fires on pagination/sort/filter,
  // not just on array length changes (which stays constant across same-size pages).
  const outreachSrIdKey = useMemo(
    () =>
      serviceCases
        .filter((sc) => sc.serviceRequest?.previousMICOutreachAttempts?.length)
        .map((sc) => sc.serviceRequest?.serviceRequestId)
        .filter(Boolean)
        .join(","),
    [serviceCases]
  );

  useEffect(() => {
    let ignore = false;

    const enrichOutreachData = async () => {
      const srIds = outreachSrIdKey.split(",").filter(Boolean);

      if (srIds.length === 0) {
        return;
      }

      try {
        const freshAttempts = await fetchOutreachAttempts({ queryParams: { serviceRequestIds: srIds } });
        if (ignore || !freshAttempts || freshAttempts.length === 0) {
          return;
        }

        const attemptsBySr = buildAttemptsBySr(freshAttempts);

        if (!ignore) {
          setServiceCases((prev) =>
            prev.map((sc) => {
              const srId = sc.serviceRequest?.serviceRequestId;
              if (!srId || !attemptsBySr[srId]) {
                return sc;
              }
              return {
                ...sc,
                serviceRequest: {
                  ...sc.serviceRequest,
                  previousMICOutreachAttempts: attemptsBySr[srId],
                },
              };
            })
          );
        }
      } catch (err) {
        // Graceful degradation — table falls back to stale embedded data.
        // Log to Sentry for observability without blocking UI.
        // Note: logger.ts JSON.stringifies multi-arg calls; Error objects serialize to "{}",
        // so we pass err.message explicitly to preserve the failure detail in Sentry.
        const errMsg = err instanceof Error ? err.message : String(err);
        logWarning(`[SupervisorDashboard] outreach enrichment failed: ${errMsg}`);
      }
    };

    enrichOutreachData();
    return () => {
      ignore = true;
    };
  }, [outreachSrIdKey, fetchOutreachAttempts]);

  useEffect(() => {
    if (serviceCaseSearchError) {
      enqueueSnackbar("Failed to get cases in queue", {
        variant: "error",
        preventDuplicate: true,
      });
    }
  }, [serviceCaseSearchError, enqueueSnackbar]);

  useEffect(() => {
    if (serviceCaseSearchCountError) {
      enqueueSnackbar("Failed to get fax cases count", {
        variant: "error",
        preventDuplicate: true,
      });
    }
  }, [serviceCaseSearchCountError, enqueueSnackbar]);

  useEffect(() => {
    if (queueVolumeError) {
      enqueueSnackbar(`Failed to load queue volume: ${queueVolumeError.message}`, { variant: "error" });
    }
  }, [enqueueSnackbar, queueVolumeError]);

  const {
    selectedIndexes,
    resetSelection,
    handleSelect,
    handleSelectAll,
    handleDeleteSelected,
    handleUnassignSelected,
    isDeleting,
    isUnassigning,
    shouldShowOnHoldWarning: shouldShowOnHoldWarningForMultiCaseAction,
  } = useMultiCaseActions(serviceCases);

  const handleDelete = async () => {
    const deletedCaseIds = await handleDeleteSelected();
    if (deletedCaseIds) {
      setServiceCases((prev) => prev.filter((sc) => !deletedCaseIds.includes(sc.id ?? "")));
    }
    serviceCaseSearch();
    setOpenMultiDelete(false);
  };

  const handleUnassign = async () => {
    const unassignedCaseIds = await handleUnassignSelected();
    if (unassignedCaseIds) {
      setServiceCases((prev) =>
        prev.map((sc) =>
          unassignedCaseIds.includes(sc.id ?? "") ? { ...sc, assignee: undefined, activeCaseHold: undefined } : sc
        )
      );
    }
    setOpenMultiUnassign(false);
  };

  const caseHoldReasonOptions = useMemo(
    () => computeHoldReasonOptions(holdReasons).map((option) => ({ ...option, id: option.label as string })),
    [holdReasons]
  );

  const makeTableColumns = () => {
    const columns = tableColumnsConfig(
      queueView,
      "SUPERVISOR_DASHBOARD",
      isCohereUser,
      queueDisplayConfig,
      missingInfoConfigs,
      enablePostClaimRetroAuths
    );
    const assigneeColumnIndex = columns.findIndex((column) => column.label === "Assignee");
    if (columns[assigneeColumnIndex]) {
      columns[assigneeColumnIndex].value = (serviceCase: ServiceCase) => {
        let assigneeLabel = serviceCase.assignee?.name || "Unassigned";

        const isCaseMarkedUnassigned =
          serviceCase?.isMarkedAsUnassigned || serviceCase.assignee?.name?.includes("Unassigned");

        if (isCaseMarkedUnassigned) {
          assigneeLabel = serviceCase.unassignReason
            ? `Unassigned - ${serviceCase.unassignReason?.shortLabel ?? "Unable to work"}`
            : "Unassigned - Unable to work";
        }

        const computedAssigneeLabel = serviceCase.activeCaseHold ? `${assigneeLabel} (on hold)` : assigneeLabel;

        let hasServiceCaseAssigneeName = serviceCase?.assignee?.name && !isCaseMarkedUnassigned;
        let isCaseMarkedUnassignedChipType: ChipType = isCaseMarkedUnassigned ? "error" : "neutral";

        const chipType: ChipType = hasServiceCaseAssigneeName ? "default" : isCaseMarkedUnassignedChipType;

        return (
          <>
            {isAssigneeDropDownOpen[serviceCase.caseNumber || ""] ? (
              <SingleSelect
                clearable
                id={serviceCase.caseNumber}
                markSelectedOptions={false}
                placeholder="Search agents"
                useOptions={useUsers}
                selectedValue={agent}
                getOptionLabel={(option: User) => {
                  return option.name + "";
                }}
                setSelectedValue={(option: User | null) => {
                  if (option === null) {
                    setAgent({ name: "" });
                    return;
                  }
                  handleOnSelect(option, serviceCase);
                }}
                openOnFocus
                onClose={() => {
                  setIsAssigneeDropDownOpen((prevValue) => {
                    return {
                      ...prevValue,
                      [serviceCase.caseNumber || ""]: false,
                    };
                  });
                }}
                TextFieldProps={{ autoFocus: true, style: { height: "40px" } }}
              />
            ) : (
              <Chip
                clickable={true}
                style={{ minWidth: "120px", margin: "8px 0px" }}
                label={computedAssigneeLabel}
                type={chipType}
                onClick={(event) => {
                  setAgent({ name: "" });
                  setSelectedServiceCase(serviceCase);
                  setIsAssigneeDropDownOpen((prevValue) => {
                    return {
                      ...prevValue,
                      [serviceCase.caseNumber || ""]: true,
                    };
                  });
                  event.stopPropagation();
                }}
                Icon={KeyboardArrowDown}
                size={"small"}
              />
            )}
          </>
        );
      };
    }

    return columns;
  };

  const tableColumns = makeTableColumns();

  const useProcedures: SelectOptionsHook<ProcedureCode | null> = (selectOptionsParams) =>
    useLazyLoadingQueryOptionsHook<any, unknown>({
      useGetHook: (args: UseGetProcedureCodesProps) => useGetProcedureCodes({ ...args, base: config.SERVICE_API_URL }),
      ...selectOptionsParams,
    });

  const useSkillSearch: SelectOptionsHook<UserSkill> = (selectOptionsParams) =>
    useLazyLoadingQueryOptionsHook<UserSkill, unknown, GetSkillsByNamePatternQueryParams>({
      ...selectOptionsParams,
      useGetHook: (args: Omit<UseGetSkillsByNamePatternProps, "path">) => useGetSkillsByNamePattern(args),
      toGetHookProps: ({ query }: LazyLoadingGetProps): GetSkillsByNamePatternQueryParams => {
        return {
          pattern: query,
        };
      },
      optionsDecorator: (opts) => {
        return [{ id: NO_REQUIRED_SKILLS_VALUE, name: NO_REQUIRED_SKILLS_VALUE } as UserSkill, ...(opts || [])];
      },
    });

  const useFacilityNpis: SelectOptionsHook<ProviderFacilityCatalogue> = (selectOptionsParams) =>
    useLazyLoadingQueryOptionsHook<ProviderFacilityCatalogue, unknown>({
      useGetHook: (args: UseGetProvidersOrFacilitiesProps) =>
        useGetProvidersOrFacilities({ ...args, base: config.SERVICE_API_URL }),
      additionalQueryParams: { recordType: "FACILITY" },
      ...selectOptionsParams,
    });

  const allowFilteringOnNewServiceCaseFields = useFeature("allowFilteringOnNewServiceCaseFields");

  const facilityBasedConfig = useConfiguration(
    "facilityBasedRequestConfiguration",
    serviceCases[0]?.healthPlan,
    serviceCases[0]?.delegatedVendor
  );

  const filterConfigDeps: FilterConfigInjectedDeps = {
    useUsers: useFilterUsers,
    usePatients: useFilterPatients,
    useProcedures,
    useSkillSearch: useSkillSearch,
    useFacilityNpis,
    authCategoriesOptions,
    allowFilteringOnNewServiceCaseFields: allowFilteringOnNewServiceCaseFields,
    enablePostClaimRetroAuths: enablePostClaimRetroAuths,
    caseHoldReasonOptions,
    facilitybasedconfig: facilityBasedConfig,
  };

  const boundTableColumnToFilterConfig = tableColumnToFilterConfig.bind(null, filterConfigDeps);
  const filterConfigs: FilterConfig[] = tableColumns
    .map((column) => boundTableColumnToFilterConfig(column, currentFilterValues))
    .filter(isNonNullable);

  const initialValues = initialFilterValues(filterConfigs, false);

  const displayMax = queryParams.max ? queryParams.max - 1 : DEFAULT_MAX_DISPLAY;

  const sortHandler = useCallback(
    (columnName: string, sortAscending: boolean) => {
      setQueryParams((prevQueryParams) => {
        const sortByColumns = optionallyIncludeEscalatedSortingCriteria([columnName], prevQueryParams.sortDesc);
        if (!prevQueryParams.sortBy?.includes(columnName)) {
          return { ...prevQueryParams, sortBy: sortByColumns, offset: 0 };
        } else {
          if (prevQueryParams.sortDesc) {
            return { ...prevQueryParams, sortBy: sortByColumns, sortDesc: undefined, offset: 0 };
          } else {
            return { ...prevQueryParams, sortBy: sortByColumns, sortDesc: true, offset: 0 };
          }
        }
      });
    },
    [optionallyIncludeEscalatedSortingCriteria, setQueryParams]
  );
  const hasAuditReviewerRole = useAuthorized("AUDIT_REVIEW");

  const massActionsOnQueuesFF = useFeature("massActionsOnQueues");

  const isManager = useAuthorized("QM_MANAGER");
  const isAssigner = useAuthorized("SUPERVISOR");
  const hasQueueManagerOrAssignerRole = isManager || isAssigner;

  const shouldDisplayDeleteButton = massActionsOnQueuesFF && massActionsEnabled && hasAuditReviewerRole;
  const shouldDisplayUnassignButton = massActionsOnQueuesFF && massActionsEnabled && hasQueueManagerOrAssignerRole;
  const shouldAllowRowSelection = shouldDisplayDeleteButton || shouldDisplayUnassignButton;
  return (
    <Grid container sx={{ mt: 5 }}>
      <Grid container sx={{ flexWrap: "nowrap" }}>
        <Box mr={8}>
          <QueueVolumeMetric queueVolumeCount={totalCount ?? queueVolume?.count} metricTitle={"Queue volume"} />
        </Box>
        <Box mr={8}>
          <TeamWorkedMetric />
        </Box>
      </Grid>
      <Grid container direction={"row"} justifyContent={"space-between"}>
        <FiltersPopover
          id={activeQueueId}
          filterConfigs={filterConfigs}
          initialValues={initialValues}
          onFiltersChange={handleFiltersChange}
          onApply={() => {
            serviceCaseSearch();
          }}
          style={{ marginTop: 5 }}
        />
        <UploadCase {...{ activeQueueId, allowUploads }} />
      </Grid>
      {shouldAllowRowSelection && selectedIndexes.length > 0 && (
        <Grid container alignItems="center" spacing={1} sx={{ mt: "8px" }}>
          <Grid item>
            <Typography variant="body1" sx={{ mr: 2, color: "text.secondary" }}>
              {selectedIndexes.length} case{selectedIndexes.length === 1 ? "" : "s"} selected
            </Typography>
          </Grid>
          <Grid item>
            {shouldDisplayDeleteButton && (
              <DeleteButton
                disabled={selectedIndexes.length === 0}
                onClick={() => setOpenMultiDelete(true)}
                startIcon={<DeleteIcon />}
                loading={isDeleting}
              >
                Delete selected
              </DeleteButton>
            )}
          </Grid>
          <Grid item>
            {shouldDisplayUnassignButton && (
              <UnassignButton
                disabled={selectedIndexes.length === 0}
                onClick={() => setOpenMultiUnassign(true)}
                startIcon={<UnassignIcon />}
                loading={isUnassigning}
                data-testid="unassign-selected-button"
              >
                Unassign selected
              </UnassignButton>
            )}
          </Grid>
        </Grid>
      )}
      <Grid container>
        <Box sx={{ width: "100%" }}>
          <CohereTable<ServiceCase>
            loading={serviceCaseSearchLoading}
            tableColumns={tableColumns}
            data={serviceCases}
            sortingColumnName={queryParams.sortBy?.filter((columnName) => columnName !== "isEscalated").at(0)}
            sortingAscending={!queryParams.sortDesc}
            onSortChange={sortHandler}
            paginateViaParams
            paginateParams={queryParams}
            setPaginateParams={setQueryParams}
            displayMax={displayMax}
            onRowClick={(serviceCase: ServiceCase, rowIndex) => {
              const caseUrl = getCaseRedirectUrl({
                caseId: serviceCase.id,
                caseType: serviceCase.caseType,
                serviceRequestId: serviceCase.serviceRequest?.serviceRequestId,
                faxId: serviceCase?.faxDetail?.attachmentId,
                isPostDenialP2P: serviceCase.isPostDenialP2P,
                fromSupervisorDashboard: true,
                queueId: activeQueueId,
                routeConfigByCaseType,
              });
              caseUrl && window.location.assign(caseUrl);
            }}
            totalCount={totalCount}
            rowHoverShadow={"0px 4px 8px 0px rgba(21, 21, 21, 0.16)"}
            hideRowHoverBorderColor={true}
            rowActiveBackgroundColor={"#F5F5F5"}
            cardHeight={"short"}
            selectableRows={shouldAllowRowSelection}
            selectedRows={selectedIndexes}
            onRowSelect={handleSelect}
            onSelectAll={handleSelectAll}
            headerMargin="16px 0px 24px 0px"
          />
        </Box>
      </Grid>
      <ReassignModal
        from={selectedServiceCase?.assignee?.name || ""}
        to={agent?.name || ""}
        open={openReassign}
        setOpen={setOpenReassign}
        reassign={async () => {
          await assignAgent(agent, selectedServiceCase);
          setOpenReassign(false);
        }}
        loading={updatingServiceCaseAssignee}
        showOnHoldWarning={!!selectedServiceCase?.activeCaseHold}
      />
      <UnassignModal
        open={openUnassign}
        setOpen={setOpenUnassign}
        unassign={async () => {
          await assignAgent(null, selectedServiceCase);
          setOpenUnassign(false);
        }}
        from={selectedServiceCase?.assignee?.name || ""}
        loading={updatingServiceCaseAssignee}
        showOnHoldWarning={!!selectedServiceCase?.activeCaseHold}
      />
      <MultiUnassignModal
        open={openMultiUnassign}
        setOpen={setOpenMultiUnassign}
        unassign={handleUnassign}
        numCases={selectedIndexes.length}
        loading={isUnassigning}
        showOnHoldWarning={shouldShowOnHoldWarningForMultiCaseAction}
      />
      <MultiDeleteModal
        open={openMultiDelete}
        setOpen={setOpenMultiDelete}
        deleteCase={handleDelete}
        numCases={selectedIndexes.length}
        loading={isDeleting}
      />
    </Grid>
  );
};

export default SupervisorDashboard;
